import { message } from "antd";
import dayjs from "dayjs";
import { LoadingLogTable } from "features/loading-log/component/table/loading-Item";
import { LoadingLogViewTopBar } from "features/loading-log/component/Top-bar/LoadingView-top-bar";
import { LoadingFilterFormValuesPaginated } from "features/loading-log/component/Top-bar/type";
import { useGetLoadingList } from "features/loading-log/use-cases/get-getLoading";
import { useApproveReject } from "features/log/use-cases/create-weighbride";
import { omit } from "lodash";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { formatDate } from "utils/date";

const PAGE_SIZE_KEY = "loading_log_page_size";

export const LoadingLog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [userEditData, setUserEditData] = useState<any>();
  const [showAppUserModal, setShowAppUserModal] = useState(false);

  /* ---------------- URL → Params ---------------- */
  const params = useMemo(() => {
    const obj = Object.fromEntries(searchParams.entries());
    const allDates = searchParams.getAll("date");

    return {
      page: Number(obj.page) || 1,
      size:
        Number(obj.size) ||
        Number(localStorage.getItem(PAGE_SIZE_KEY)) ||
        10,
      MaterialName: obj.MaterialName || undefined,
      DriverName: obj.DriverName || undefined,
      VehicleNumber: obj.VehicleNumber || undefined,
      WeightType: obj.WeightType || undefined,

      // 🚨 keep date as string[] here
      date: allDates.length ? allDates : undefined,
    };
  }, [searchParams]);

  /* ---------------- Prepare API Payload ---------------- */
  const payload = useMemo(() => {
    const [start, end] = Array.isArray(params.date) ? params.date : [];

    const startDate = start ? dayjs(start) : null;
    const endDate = end ? dayjs(end) : null;

    return {
      ...omit(params, ["date"]),
      LoadingStartDate:
        startDate && startDate.isValid()
          ? formatDate({
              date: startDate.startOf("day"),
              format: "dbDate",
            })
          : undefined,
      LoadingEndDate:
        endDate && endDate.isValid()
          ? formatDate({
              date: endDate.endOf("day"),
              format: "dbDate",
            })
          : undefined,
    };
  }, [params]);

  /* ---------------- API ---------------- */
  const getLoadingListQueryResponse = useGetLoadingList(payload, {
    retry: false,
  });

  /* ---------------- Approve / Reject ---------------- */
  const { mutate } = useApproveReject();

  const onApproveReject = (log: any, isApproved: number) => {
    mutate(
      {
        EntryId: log?.entryId,
        Type: log?.entryType,
        IsApproved: isApproved === 1 ? 1 : 2,
      },
      {
        onError: () => {
          message.error("Something went wrong, please try again.");
        },
      }
    );
  };

  /* ---------------- Filters ---------------- */
  const handleFiltersChange = (values: LoadingFilterFormValuesPaginated) => {
    const search: Record<string, string | string[]> = {
      page: "1",
      size: params.size.toString(),
    };

    Object.entries(values).forEach(([key, value]) => {
      if (!value) return;

      if (key === "date" && Array.isArray(value)) {
        // ✅ convert dayjs → string
        search.date = value.map((d:any) => d.toISOString());
      } else {
        search[key] = String(value);
      }
    });

    setSearchParams(search);
  };

  /* ---------------- Pagination ---------------- */
  const handleTableChange = (newParams: { page: number; size: number }) => {
    localStorage.setItem(PAGE_SIZE_KEY, newParams.size.toString());

    const currentFilters = Object.fromEntries(
      Object.entries(params).filter(
        ([key, v]) => key !== "page" && key !== "size" && v !== undefined
      )
    );

    setSearchParams({
      ...currentFilters,
      page: String(newParams.page),
      size: String(newParams.size),
    });
  };

  /* ---------------- Initial Filters (🔥 FIXED) ---------------- */
  const getInitialFilterValues = {
    MaterialName: params.MaterialName,
    DriverName: params.DriverName,
    VehicleNumber: params.VehicleNumber,
    WeightType: params.WeightType,

    // ✅ string[] → dayjs[]
    date: Array.isArray(params.date)
      ? params.date.map((d: string) => dayjs(d))
      : undefined,
  };

  return (
    <>
      <LoadingLogViewTopBar
        getInitialFilterValues={getInitialFilterValues as any}
        handleFilter={handleFiltersChange}
        userEditData={userEditData}
        showAppUserModal={showAppUserModal}
        setShowAppUserModal={setShowAppUserModal}
        onRefresh={getLoadingListQueryResponse.refetch}
      />

      <LoadingLogTable
        isListLoading={getLoadingListQueryResponse.isFetching}
        vehicleList={getLoadingListQueryResponse?.data?.res?.items}
        itemsCount={getLoadingListQueryResponse?.data?.totalRecords || 0}
        tableParams={params as any}
        updateParams={handleTableChange as any}
        setUserEditData={setUserEditData}
        setShowAppUserModal={setShowAppUserModal}
        onApproveReject={onApproveReject}
        onRefresh={getLoadingListQueryResponse.refetch}
        isFetching={getLoadingListQueryResponse.isFetching}
      />
    </>
  );
};
